import {
  PersonalCalendarEventCreateFields,
  PersonalCalendarEventUpdateFields,
  CalendarEventCreateFields,
  CalendarEventUpdateFields,
  dataSchema,
} from '@ragu-components/ord-types';
import { DateTime } from 'luxon';

import type { CalendarEventFields } from '../types';
import type { ObjectJsonSchema } from '@ragu-components/json-schema-context';

type DateType = 'start' | 'end';
type DateInclusionType = 'inclusive' | 'exclusive';

/** used to convert the dates of an event before sending it to server. If it's an allday event it also sets the time portion of the date accordingly */
export const dateConverterFromInputToServer = (
  date: string,
  allDay: boolean,
  dateType: DateType,
  timezone: string
): string | undefined => {
  const matches = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?/.test(date);
  if (matches) {
    if (allDay) {
      // For all-day events, work with UTC dates without timezone conversion
      let dateTime: DateTime;

      const isISOString = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.*Z$/.test(date);
      if (isISOString) {
        // Parse ISO string and convert to UTC
        dateTime = DateTime.fromISO(date).toUTC();
      } else {
        // Parse date string as UTC date
        const dateOnly = date.split('T')[0]; // Extract date part
        if (dateOnly === undefined || dateOnly === '') return undefined;
        dateTime = DateTime.fromISO(dateOnly, { zone: 'utc' });
        if (!dateTime.isValid) return undefined;
      }

      // Set time based on dateType
      if (dateType === 'start') {
        // Start: beginning of day (00:00:00 UTC)
        dateTime = dateTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
      } else {
        // End: beginning of next day (00:00:00 UTC next day) for proper all-day event
        dateTime = dateTime.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).plus({ days: 1 });
      }

      const isoString = dateTime.toISO();
      if (isoString === null || isoString === undefined) return undefined;
      // Remove milliseconds to match expected format
      return isoString.replace(/\.\d{3}Z$/, 'Z');
    } else {
      // For non-all-day events, use timezone conversion
      const dateTz = setTimezone(date, timezone);
      if (!dateTz.isValid) return undefined;
      const isoString = dateTz.toISO();
      if (isoString === null || isoString === undefined) return undefined;
      const fixedDate = new Date(isoString);
      return parse(fixedDate);
    }
  }
  return undefined;
};

/** used to parse the received date of an event and converting it in an exclusive date fullcalendar can use in case of an allday event */
export const dateConverterFromServerToFC = (date: string, type: DateInclusionType): string => {
  if (type === 'inclusive') return DateTime.fromISO(date).toLocal().toFormat('yyyy-MM-dd');
  else {
    const convertedDate = DateTime.fromISO(date);
    const exclusiveDate = convertedDate.plus({ days: 1 });
    return exclusiveDate.toFormat('yyyy-MM-dd');
  }
};

/** formats the preprocessed in callback date to be used within the calendar */
export const dateConverterFromInputToFC = (
  date: string,
  timezone: string,
  allDay: boolean | undefined,
  forRruleDtstart: boolean | undefined = false
): string => {
  // Return empty string if date is null or empty
  if (date == null || date === '') {
    return '';
  }

  // If it's an all-day event, return the date as is (FullCalendar handles these without timezones)
  if (allDay === true) {
    return date; // e.g., 'YYYY-MM-DD'
  }

  // If this is specifically for rrule.dtstart and we have a timezone,
  // return the original date string as a local ISO format.
  // FullCalendar's rrule plugin can then use this with rrule.tzid
  // to maintain local time across DST changes.
  if (forRruleDtstart && timezone) {
    // Parse the date to ensure it's valid, then format it without timezone info.
    // This allows FullCalendar to interpret it as a "floating" time in the context of rrule.tzid.
    const parsedDate = DateTime.fromISO(date, { zone: timezone });
    if (!parsedDate.isValid) {
      return '';
    }
    // Return just the date and time part (e.g., '2025-03-01T08:00:00'),
    // so FullCalendar interprets it as local in tzid.
    return parsedDate.toFormat("yyyy-MM-dd'T'HH:mm:ss");
  }

  // For all other timed events (non-all-day, or non-rrule.dtstart context),
  // convert the date from its specified timezone to UTC.
  // This ensures a universal, unambiguous point in time, suitable for `event.start`.
  const localizedDate = DateTime.fromISO(date, { zone: timezone });

  // Check if luxon successfully parsed the date.
  if (!localizedDate.isValid) {
    return '';
  }

  // Convert the localized date to UTC and then to an ISO string with 'Z' suffix.
  // This is the ideal format for `event.start` when FullCalendar's overall `timeZone`
  // option (e.g., 'local') is used to display the event in the user's local time.
  const convertedDateToUTC = localizedDate.toUTC();

  const result = convertedDateToUTC.toISO();

  if (result === null) {
    return '';
  }

  return result;
};

/** formats the end date to be used within the dateinput component (makes it inclusive instead of the exclusive used by fullcalendar) */
export const dateFormatterFromFCToInput = (date: string, allDay: boolean): string => {
  if (date == null || date === '') {
    return '';
  }

  return allDay ? formatter(new Date(date), true) : date;
};

/** formats the dates to be used within the dateinput component (converting utc to local as if local was timezone) */
export const dateConverterFromServerToInput = (
  date: string,
  timezone: string,
  type: DateInclusionType = 'inclusive'
): string => {
  // Parse UTC date and convert to target timezone, then format as local time
  const targetDateTime = DateTime.fromISO(date).setZone(timezone);
  if (type === 'exclusive') {
    return targetDateTime.minus({ days: 1 }).toFormat("yyyy-MM-dd'T'HH:mm");
  }
  return targetDateTime.toFormat("yyyy-MM-dd'T'HH:mm");
};

export const buildSchema = (isCreate: boolean, isPersonalResource: boolean): ObjectJsonSchema => {
  const jsonSchema = isPersonalResource
    ? isCreate
      ? dataSchema.PersonalCalendarEventCreate
      : dataSchema.PersonalCalendarEventUpdate
    : isCreate
      ? dataSchema.CalendarEventCreate
      : dataSchema.CalendarEventUpdate;

  const calendarIdProperty = dataSchema.MultiCalendarEvent.properties.calendar.properties.id;

  const fixedJsonSchema = {
    ...jsonSchema,
    properties: {
      ...jsonSchema.properties,
      rrule: {
        ...jsonSchema.properties.rrule,
        oneOf: [...jsonSchema.properties.rrule.oneOf, { type: 'null' }],
      },
    },
  };

  const extendedJsonSchema = isCreate
    ? {
        ...fixedJsonSchema,
        properties: {
          ...fixedJsonSchema.properties,
          calendar_id: calendarIdProperty,
        },
        required: [
          ...(jsonSchema as typeof dataSchema.CalendarEventCreate | typeof dataSchema.PersonalCalendarEventCreate)
            .required,
          'calendar_id',
        ],
      }
    : fixedJsonSchema;

  return extendedJsonSchema;
};

export const buildFields = (isCreate: boolean, isPersonalResource: boolean): CalendarEventFields => {
  return isPersonalResource
    ? isCreate
      ? PersonalCalendarEventCreateFields
      : PersonalCalendarEventUpdateFields
    : isCreate
      ? CalendarEventCreateFields
      : CalendarEventUpdateFields;
};

const setTimezone = (date: string, timezone: string): DateTime => {
  const dateAdapted = date.replace('Z', '').replace(/[+-]\d{2}:\d{2}/, '');
  // Parse as local time in the target timezone - this creates a DateTime in that timezone
  return DateTime.fromISO(dateAdapted, { zone: timezone });
};

const parse = (date: Date): string | undefined => {
  const isoString = `${date.toISOString().slice(0, -5)}Z`;
  return isoString;
};

const formatter = (value: Date, fromFC: boolean): string => {
  if (!(value instanceof Date) || isNaN(value.getDate())) return '';
  const pad = '00';
  if (fromFC) value.setDate(value.getDate() - 1);
  const yyyy = value.getFullYear().toString();
  const MM = (value.getMonth() + 1).toString();
  const dd = value.getDate().toString();
  const formattedString = `${yyyy}-${(pad + MM).slice(-2)}-${(pad + dd).slice(-2)}`;
  return formattedString;
};

/** Parser function to convert input value (Date) to the string format required by the server */
export const dateInputValueParser = (
  valueFromInput: string | Date | null | undefined,
  allDay: boolean
): string | null | undefined => {
  if (valueFromInput instanceof Date) {
    if (allDay) {
      // Return date in YYYY-MM-DD format for all-day events
      const year = valueFromInput.getFullYear();
      const month = String(valueFromInput.getMonth() + 1).padStart(2, '0');
      const day = String(valueFromInput.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } else {
      // Return full date-time in ISO format for non-all-day events
      const year = valueFromInput.getFullYear();
      const month = String(valueFromInput.getMonth() + 1).padStart(2, '0');
      const day = String(valueFromInput.getDate()).padStart(2, '0');
      const hours = String(valueFromInput.getHours()).padStart(2, '0');
      const minutes = String(valueFromInput.getMinutes()).padStart(2, '0');
      const seconds = String(valueFromInput.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    }
  }
  return valueFromInput;
};
